import { useEffect, useRef, useState } from 'react';
import SeoMeta from '../../components/SeoMeta/SeoMeta';
import { useAuth } from '../auth/hooks/useAuth';
import usePublicPage from '../auth/hooks/usePublicPage';
import { useTheme, nextTheme, THEME_LABELS } from '../../context/ThemeContext';
import ThemeModeIcon from '../../components/ThemeModeIcon/ThemeModeIcon';
import '../marketing/tokens.css';
import './IvoriLandingPage.css';
import CookieSettingsButton from '../consent/CookieSettingsButton';

// The company home at ivoriai.com/about (ivoriai.com/ is the Edu home):
// three full-screen slides that snap into place. The company line, then one
// invitation per destination. It renders outside the /edu router basename, so
// links into Edu are plain <a href> with the prefix written out (a separate
// page load), never <Link>.
// Design: docs/features/ivori-main-page/README.md.

const DESTINATIONS = [
  {
    id: 'edu',
    name: 'Edu',
    eyebrow: 'For students and schools',
    line: 'Master any exam. Master any interview.',
    cta: 'Enter IVORI Edu',
    href: '/',
    ground: 'bg',
  },
  {
    id: 'lab',
    name: 'Lab',
    eyebrow: 'Building the Decision World Model',
    line: 'Language Models predict words. IVORI understands worlds.',
    cta: 'Visit IVORI Lab',
    href: 'https://ivorilab.com',
    external: true,
    ground: 'ink',
  },
];

// The legal pages live in the edu app (LegalHubLayout, Ivori-branded), the
// same four pathbuddy.com's footer links to. Same tab: they are IVORI's own.
const LEGAL_LINKS = [
  { label: 'Privacy', href: '/edu/privacy' },
  { label: 'Terms', href: '/edu/terms-of-use' },
  { label: 'Safeguarding', href: '/edu/safeguarding' },
  { label: 'Cookies', href: '/edu/cookies' },
];

const SLIDES = [{ id: 'ivori', label: 'IVORI', ground: 'bg' }].concat(
  DESTINATIONS.map((d) => ({ id: d.id, label: d.name, ground: d.ground }))
);

// Scroll snapping is set on <html>, so it is added for this page only and
// removed when the page unmounts; it must never reach the Edu pages.
const SNAP_CLASS = 'ivori-co-snap';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://ivoriai.com/#organization',
      name: 'IVORI',
      alternateName: 'IVORI AI',
      url: 'https://ivoriai.com/',
      description: 'AI for decision-makers, from the classroom to the boardroom.',
      sameAs: ['https://linkedin.com/company/ivorilab/'],
      department: {
        '@type': 'Organization',
        name: 'IVORI Lab',
        url: 'https://ivorilab.com',
        description: 'Building the Decision World Model.',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://ivoriai.com/#website',
      name: 'IVORI',
      alternateName: 'IVORI AI',
      url: 'https://ivoriai.com/',
      inLanguage: 'en-GB',
      publisher: {
        '@id': 'https://ivoriai.com/#organization',
      },
    },
    {
      '@type': 'SoftwareApplication',
      name: 'IVORI Edu',
      url: 'https://ivoriai.com/',
      applicationCategory: 'EducationalApplication',
      operatingSystem: 'Web',
      description: 'Master any exam. Master any interview. AI for students and schools.',
      publisher: {
        '@id': 'https://ivoriai.com/#organization',
      },
    },
  ],
};

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function scrollToSlide(id) {
  const target = document.getElementById(id);
  if (target) target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const upcoming = nextTheme(theme);
  const label = `Switch to ${THEME_LABELS[upcoming].toLowerCase()} mode`;
  return (
    <button type="button" className="ivori-co-theme" onClick={toggleTheme} aria-label={label} title={label}>
      <ThemeModeIcon mode={upcoming} />
    </button>
  );
}

function Lockup({ name, id }) {
  return (
    <h2 className="ivori-co-lockup" id={id} aria-label={`IVORI ${name}`}>
      <span className="ivori-co-lockup-co ivori-co-rs" aria-hidden="true">IVORI</span>
      <span className="ivori-co-lockup-name" aria-hidden="true">
        {name.split('').map((letter, i) => (
          <span key={`${letter}-${i}`} className="ivori-co-letter">{letter}</span>
        ))}
        <span className="ivori-co-caret" />
      </span>
    </h2>
  );
}

export default function IvoriLandingPage() {
  usePublicPage();
  const { isAuthenticated } = useAuth();
  const [active, setActive] = useState('ivori');
  const [observed, setObserved] = useState(true);
  const slideRefs = useRef({});

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add(SNAP_CLASS);
    return () => root.classList.remove(SNAP_CLASS);
  }, []);

  // The slide across the middle of the viewport is "in": it plays its arrival,
  // the pager marks it and the header takes its ground. A centre line rather
  // than a visibility ratio, so a slide taller than the screen (a phone on its
  // side) still arrives. Without IntersectionObserver every slide shows in its
  // final state.
  useEffect(() => {
    if (typeof window.IntersectionObserver !== 'function') {
      setObserved(false);
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    Object.values(slideRefs.current).forEach((node) => {
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, []);

  const ground = (SLIDES.find((s) => s.id === active) || SLIDES[0]).ground;
  const slideClass = (id, extra) =>
    ['ivori-co-slide', extra, !observed || active === id ? 'is-in' : null].filter(Boolean).join(' ');
  const setRef = (id) => (node) => {
    slideRefs.current[id] = node;
  };

  return (
    <div className="mkt ivori-co" data-on={ground}>
      <SeoMeta
        title="IVORI — AI for Decision-Makers | IVORI Edu"
        description="IVORI is AI for decision-makers, from the classroom to the boardroom. IVORI Edu for students and schools, built on research from IVORI Lab."
        keywords="IVORI, IVORI Edu, IVORI Lab, Decision World Model, AI for decision-makers, admissions AI"
        url="https://ivoriai.com/about"
        structuredData={jsonLd}
      />

      <header className="ivori-co-header">
        <div className="ivori-co-wrap ivori-co-header-bar">
          <a className="ivori-co-wordmark" href="/about" aria-label="IVORI home">IVORI</a>
          <div className="ivori-co-actions">
            <a className="ivori-co-to-edu" href="/" aria-label="IVORI Edu">
              <span className="ivori-co-to-edu-co">IVORI</span>
              <em>Edu</em>
            </a>
            <span className="ivori-co-rule" aria-hidden="true" />
            <ThemeToggle />
            {isAuthenticated ? (
              <a className="ivori-co-signin" href="/edu/chat">Open app</a>
            ) : (
              <a className="ivori-co-signin" href="/edu/login">Sign in</a>
            )}
          </div>
        </div>
      </header>

      <nav className="ivori-co-pager" aria-label="Slides">
        {SLIDES.map((slide) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => scrollToSlide(slide.id)}
            aria-current={active === slide.id ? 'true' : undefined}
          >
            <span>{slide.label}</span>
            <i aria-hidden="true" />
          </button>
        ))}
      </nav>

      <main>
        <section className={slideClass('ivori')} id="ivori" ref={setRef('ivori')} aria-label="IVORI">
          <div className="ivori-co-glow" aria-hidden="true" />
          <div className="ivori-co-wrap ivori-co-body">
            <h1 className="ivori-co-statement ivori-co-rs">
              <span>
                AI for <span className="ivori-co-nowrap">decision-makers</span>
              </span>
              <span className="ivori-co-muted">From the classroom to the boardroom.</span>
            </h1>
            <div className="ivori-co-ways ivori-co-rs ivori-co-rs-3">
              {DESTINATIONS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="ivori-co-way"
                  aria-label={`IVORI ${d.name}`}
                  onClick={() => scrollToSlide(d.id)}
                >
                  <span className="ivori-co-way-co">IVORI</span>
                  <em>{d.name}</em>
                </button>
              ))}
            </div>
          </div>
        </section>

        {DESTINATIONS.map((d) => (
          <section
            key={d.id}
            className={slideClass(d.id, d.ground === 'ink' ? 'ivori-co-ink' : null)}
            id={d.id}
            ref={setRef(d.id)}
            aria-labelledby={`${d.id}-title`}
          >
            <div className="ivori-co-wrap ivori-co-body">
              <p className="ivori-co-eyebrow ivori-co-rs">{d.eyebrow}</p>
              <Lockup name={d.name} id={`${d.id}-title`} />
              <p className="ivori-co-line ivori-co-rs ivori-co-rs-4">{d.line}</p>
              {d.external ? (
                <a className="ivori-co-btn ivori-co-rs ivori-co-rs-5" href={d.href} target="_blank" rel="noopener noreferrer">
                  {d.cta} <span aria-hidden="true">↗</span>
                </a>
              ) : (
                <a className="ivori-co-btn ivori-co-rs ivori-co-rs-5" href={d.href}>{d.cta}</a>
              )}
            </div>
            {d.id === 'lab' ? (
              <footer className="ivori-co-wrap ivori-co-foot">
                <span>© 2026 IVORI</span>
                <a href="https://www.linkedin.com/company/ivorilab/posts/?feedView=all" target="_blank" rel="noopener noreferrer">
                  IVORI Lab on LinkedIn <span aria-hidden="true">↗</span>
                </a>
                <a href="https://www.linkedin.com/showcase/ivoriedu/posts/?feedView=all" target="_blank" rel="noopener noreferrer">
                  IVORI Edu on LinkedIn <span aria-hidden="true">↗</span>
                </a>
                {LEGAL_LINKS.map((l) => (
                  <a key={l.href} href={l.href}>{l.label}</a>
                ))}
                <CookieSettingsButton />
              </footer>
            ) : null}
          </section>
        ))}
      </main>
    </div>
  );
}
